/** 계정 쪽 화면이 읽는 값. 마이페이지(`A-40`·`W-70`), 환경설정(`A-40b`·`A-40c`·`A-40d`·`W-80`),
 * 사진 이용(`A-41`), 내 데이터(`A-42`).
 *
 * **설정은 계정 단위다.** `W-80` 이 그 이유를 적어 뒀다. 앱에서 한 번 정하면 웹에도
 * 적용되고, 같은 값을 두 자리에서 만지게 하면 어느 쪽이 이겼는지 셀러가 알 수 없다.
 * 그래서 이 파일의 어느 타입에도 기기 식별자가 없다. 화면이 기기별 값을 만질 방법이
 * 타입에 없어야 그 규칙이 지켜진다.
 *
 * D1 도 같은 모양이 됐다. 0002 는 `(sellerId, deviceKey)` 로 계정별과 기기별을 동시에
 * 담았는데(그때는 미정이었다) `0004_export_size_settings_notification.sql` 이 `deviceKey`
 * 를 걷어내고 유일 제약을 `sellerId` 단독으로 바꿨다. 컬럼이 없어야 코드 한 줄이 규칙을
 * 깨는 일이 안 생긴다.
 *
 * ⚠️ 0004 는 파일로만 있고 아직 적용 전이다. 적용 전까지 서버는 `deviceKey = ''` 행
 * 하나만 읽고 쓴다.
 */

import type { ConsentDocType, IdentityProvider } from "./domain";

// ── 산출물 배경 ──────────────────────────────────────────────────────────────

/** 기본 배경 팔레트(`A-40c`·`W-80`). **산출물의 배경색이지 앱 화면 색이 아니다.**
 *
 * 정본은 `docs/TEMPLATE.md` 4장이고 지금 정해진 것은 앞의 둘뿐이다. 뒤 둘은 자리만 있고
 * 값이 없어 `hex` 가 `null` 이다. 임의로 채우면 그 값이 코드에 눌러앉는다. */
export interface BackgroundOption {
  key: string;
  /** 화면 문구. */
  label: string;
  /** 확정 전이면 `null`. 화면은 이때 `미정` 으로 그린다. */
  hex: string | null;
}

export const BACKGROUND_OPTIONS: readonly BackgroundOption[] = [
  { key: "WHITE", label: "흰색", hex: "#FFFFFF" },
  { key: "LIGHT_GRAY", label: "연회색", hex: "#F5F5F7" },
  { key: "IVORY", label: "아이보리", hex: null },
  { key: "COOL_GRAY", label: "쿨그레이", hex: null },
] as const;

export const DEFAULT_BACKGROUND_HEX = "#FFFFFF";

/** 서버가 **실제로 저장을 허용하는** 배경값. 위 팔레트에서 `hex` 가 정해진 것만 추린다.
 *
 * ⛔ **색값을 여기 손으로 다시 적지 마라.** 위 배열에서 뽑는 것이 요점이다 — 두 벌이 되면
 * 화면에는 안 보이는 색이 저장은 되거나, 화면에 보이는 색이 서버에서 422 로 거절된다.
 * 아이보리·쿨그레이가 확정되면(PRD `7.13` D-2) 위 배열의 `hex` 를 채우는 것만으로 이 목록이
 * 함께 넓어진다.
 *
 * **왜 `channel.ts` 가 아닌가.** 배경은 지금 **계정 설정**에서 고르는 값이고(`A-40c`·`W-80`),
 * 채널 규격이 정하는 것은 캔버스·블록·파일 이름·내보내기 형식뿐이다(`channel.ts` 머리말).
 * 판매처마다 팔레트가 갈리는 날 할 일은 아래 함수에 채널 인자를 더해 목록을 규격에서 읽게
 * 하는 것이고, **그때 고칠 자리가 이 한 함수로 남는다.** */
export const ALLOWED_BACKGROUND_HEXES: readonly string[] = BACKGROUND_OPTIONS.map((o) => o.hex)
  .filter((hex): hex is string => hex !== null);

/** 셀러가 보낸 배경값을 **저장할 수 있는 한 가지 표기**로 맞춘다. 팔레트 밖이면 `null`.
 *
 * 대소문자를 여기서 흡수하는 이유: `#ffffff` 와 `#FFFFFF` 가 둘 다 저장되면 **같은 색이 두
 * 값이 되고**, 동결 스냅샷(`editStateJson`)의 글자가 갈려 **같은 상태의 판이 다른 판으로
 * 판정된다**(`sameVersionState` 는 문자열을 그대로 비교한다). 곧 승인·내보내기 멱등이
 * 대소문자 하나로 깨진다. */
export function normalizeBackgroundColor(raw: string): string | null {
  const hex = raw.trim().toUpperCase();
  return ALLOWED_BACKGROUND_HEXES.includes(hex) ? hex : null;
}

/** 저장된 hex 를 화면 문구로. 팔레트 밖 값이면 hex 를 그대로 보여준다.
 *  틀린 이름을 붙이는 것보다 낫다. */
export function backgroundLabel(hex: string): string {
  return BACKGROUND_OPTIONS.find((o) => o.hex === hex)?.label ?? hex;
}

// ── 환경설정 ─────────────────────────────────────────────────────────────────

/** `HH:MM` 로컬 시각. `A-40d` 가 10분 단위로만 고르게 한다. */
export type ClockTime = string;

/** 알림을 보내지 않는 구간. **두 끝을 한 값으로 다룬다**. `A-40d` 가 시트 하나에서 시작과
 *  끝을 함께 고치는 이유다. 한쪽만 고칠 수 있으면 `22:00 – 08:00` 을 정하는 데 두 번 연다. */
export interface QuietHours {
  enabled: boolean;
  start: ClockTime;
  end: ClockTime;
}

/** 분 단위 눈금. 굴릴 칸이 60에서 6으로 줄어 목표 값에 빨리 닿는다(`A-40d`). */
export const QUIET_HOURS_MINUTE_STEP = 10;

/** 환경설정 전체. **앱과 웹이 같은 값을 읽는다.**
 *
 * 웹(`W-80`)은 이 중 `defaultBackgroundColor` 만 그린다. `wifiOnlyUpload` 는 PC 가 종량
 * 회선이 아니라서, `saveExportToGallery` 는 받은 파일을 브라우저가 알아서 둬서, 알림
 * 스위치는 웹 푸시가 미정이라 자리를 두지 않았다. **값이 없는 게 아니라 그리지 않는
 * 것이다**. 앱에서 켜 둔 값은 그대로 살아 있다. */
export interface SellerSettingsView {
  /** 산출물 배경 기본값. 새 작업이 시작할 때 쓰고 작업마다 고치기에서 바꾼다. */
  defaultBackgroundColor: string;
  wifiOnlyUpload: boolean;
  saveExportToGallery: boolean;

  notifyOnComplete: boolean;
  notifyOnFailureOnly: boolean;
  quietHours: QuietHours;

  /** 유의사항 블록 문구. 전 상품 재사용이라 작업이 아니라 계정에 붙는다. */
  noticeText: string | null;
}

/** 스위치 하나만 바꾸는 요청. **전체를 다시 보내지 않는다**. 앱과 웹이 같은 행을 만지므로
 *  전체 덮어쓰기를 하면 다른 기기에서 방금 바꾼 값을 되돌린다. */
export type SellerSettingsPatch = Partial<SellerSettingsView>;

export const DEFAULT_SELLER_SETTINGS: SellerSettingsView = {
  defaultBackgroundColor: DEFAULT_BACKGROUND_HEX,
  wifiOnlyUpload: true,
  saveExportToGallery: false,
  notifyOnComplete: true,
  notifyOnFailureOnly: false,
  quietHours: { enabled: true, start: "22:00", end: "08:00" },
  noticeText: null,
};

// ── 사진 이용과 동의 기록 ────────────────────────────────────────────────────

/** 동의 기록 한 줄(`A-41`·`W-70`). 약관 판이 바뀌면 갱신이 아니라 줄이 하나 더 쌓인다. */
export interface ConsentRecordView {
  docType: ConsentDocType;
  /** 화면 문구. `이용약관` · `개인정보처리방침`. */
  docLabel: string;
  /** `v1.2`. */
  docVersion: string;
  agreedAt: string;
  /** 철회했으면 그 시각. `A-41` 이 없을 때 `철회 없음`으로 적는다. */
  withdrawnAt: string | null;
}

/** 사진 이용 화면(`A-41`) 전체.
 *
 * `optIn` 이 `null` 인 것은 "아직 정하지 않음"이다. 기본값을 옵트인으로 할지 옵트아웃으로
 * 할지가 제품 결정이고 승인 전이라(`A-41` 하단 미정 노트) `false` 로 눌러 두면 그 결정을
 * 코드가 먼저 내려 버린다. */
export interface TrainingUseView {
  optIn: boolean | null;
  optInChangedAt: string | null;
  consents: ConsentRecordView[];
}

// ── 마이페이지 요약 ──────────────────────────────────────────────────────────

/** `A-40`·`W-70` 의 요약 줄. **같은 숫자를 한 화면에 두 번 적지 않는다**는 규칙 때문에
 * 이 형태에 이번 달 쓴 금액이 따로 없다. `결제 이력` 줄이 `9월 ₩270` 으로 이미 말한다.
 *
 * (웹 `W-70` 은 폭이 있어 `쓴 금액 ₩270` 을 요약에 함께 적는다. 같은 값이므로 필드를
 * 늘리지 않고 `monthlyBilledAmount` 하나를 양쪽이 쓴다.) */
export interface MyPageSummaryView {
  displayName: string | null;
  /** 어느 수단으로 들어왔는지. 헤더 아이콘이 아니라 글로 밝힌다(`W-60` 계정 메뉴). */
  provider: IdentityProvider | null;
  joinedAt: string;

  /** 이번 달 만든 상세페이지 건수와 장수. */
  monthlyJobCount: number;
  monthlyImageCount: number;
  /** 이번 달 청구 합계. 정본은 청구 항목 합계다. */
  monthlyBilledAmount: number;

  /** `작업 이력 관리 18건`. 지운 것을 뺀 전체. */
  totalJobCount: number;
  /** `내 데이터 142장`. **아직 파기하지 않은 원본만 센다**. 지운 장을 세면 셀러가
   *  지우기가 안 먹었다고 읽는다. */
  storedSourceImageCount: number;

  trainingUseOptIn: boolean | null;
}
